import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Play, ShieldCheck, Users } from "lucide-react";
import "./Hero.css";

export default function Hero() {
  const heroRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;
    const observer = new IntersectionObserver(([entry]) => {
      document.body.classList.toggle("home-hero-active", entry.isIntersecting);
    }, { threshold: 0.2 });
    observer.observe(hero);
    return () => {
      observer.disconnect();
      document.body.classList.remove("home-hero-active");
    };
  }, []);

  return (
    <section ref={heroRef} className="edx-scene" data-testid="hero-section" aria-labelledby="home-heading">
      {/* The artwork moves as one layer so every sign stays aligned with the building. */}
      <div className="edx-scene-visual">
        <img className="edx-scene-art" src="/edenox-hero-office-v6.png"
          alt="EDENOX's Johannesburg technology studio with illuminated software, AI, cloud and digital product displays."
          width="1672" height="941" fetchPriority="high" />
      </div>
      <div className="edx-scene-light edx-scene-light-cyan" aria-hidden="true" />
      <div className="edx-scene-light edx-scene-light-magenta" aria-hidden="true" />
      <div className="edx-scene-scan" aria-hidden="true" />
      <div className="edx-scene-shade" aria-hidden="true" />
      <div className="edx-scene-frame">
        <div className="edx-scene-copy">
          <p className="edx-scene-kicker">DIGITAL SYSTEMS STUDIO <span>/</span> JOHANNESBURG</p>
          <h1 id="home-heading" className="edx-scene-title" data-testid="hero-title">
            <span>Digital systems</span>
            <span>built for</span>
            <span className="edx-scene-gradient">real growth.</span>
          </h1>
          <div className="edx-scene-lede">
            <p>
              EDENOX turns bold ideas into powerful digital solutions built to move businesses forward. From custom software and AI systems
              to digital products and cloud infrastructure, we create technology that is smart, scalable, and built around real business goals.
            </p>
            <p>
              Whether you’re launching, modernising, or ready to scale, we help you move faster with solutions designed to make an impact.{" "}
              <strong>You bring the vision — we build what’s next.</strong>{" "}
              And we build it to grow with your business, not hold it back.
            </p>
          </div>
          <div className="edx-scene-actions">
            <Link to="/consultation" className="edx-scene-primary" data-testid="hero-cta-book">
              Start a project <ArrowRight aria-hidden="true" />
            </Link>
            <Link to="/the-archive" className="edx-scene-secondary" data-testid="hero-cta-pricing">
              See our work <span className="edx-scene-play"><Play aria-hidden="true" /></span>
            </Link>
          </div>
        </div>
        <div className="edx-scene-status" data-testid="hero-stats">
          <div>
            <span>01 / STATUS</span>
            <strong><i className="edx-scene-live" />Accepting projects</strong>
            <small>NEW OPPORTUNITIES</small>
          </div>
          <div>
            <span>02 / CREDENTIAL</span>
            <strong><ShieldCheck aria-hidden="true" />Level 1 B-BBEE</strong>
            <small>VERIFIED &amp; COMPLIANT</small>
          </div>
          <div>
            <span>03 / OWNERSHIP</span>
            <strong><Users aria-hidden="true" />100% Black female-owned</strong>
            <small>DRIVING INCLUSIVE GROWTH</small>
          </div>
        </div>
      </div>
    </section>
  );
}
